import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import './team-attendance-pager.component.css';

export const TEAMS_PER_PAGE = 4;

interface TeamAttendancePagerProps<T> {
  items: T[];
  gridClassName: string;
  getItemKey: (item: T) => string | number;
  renderItem: (item: T, index: number) => ReactNode;
  title?: string;
  titleClassName?: string;
}

function TeamAttendancePager<T>({
  items,
  gridClassName,
  getItemKey,
  renderItem,
  title,
  titleClassName = 'tab-team-pager-title',
}: TeamAttendancePagerProps<T>) {
  const [page, setPage] = useState(0);
  const itemsKey = useMemo(() => items.map(getItemKey).join(','), [items]);
  const totalPages = Math.max(1, Math.ceil(items.length / TEAMS_PER_PAGE));
  const showNav = items.length > TEAMS_PER_PAGE;

  useEffect(() => {
    setPage(0);
  }, [itemsKey]);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const pageStart = page * TEAMS_PER_PAGE;
  const visibleItems = showNav ? items.slice(pageStart, pageStart + TEAMS_PER_PAGE) : items;

  return (
    <div className="tab-team-pager-section">
      {(title || showNav) && (
        <div className={`tab-team-pager-header ${!title ? 'tab-team-pager-header--nav-only' : ''}`}>
          {title ? <div className={titleClassName}>{title}</div> : null}
          {showNav ? (
            <div className="tab-team-pager-nav">
              <button
                type="button"
                className="tab-team-pager-btn"
                onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                disabled={page === 0}
                aria-label="Previous teams"
              >
                <ChevronLeftIcon fontSize="small" />
              </button>
              <button
                type="button"
                className="tab-team-pager-btn"
                onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                disabled={page >= totalPages - 1}
                aria-label="Next teams"
              >
                <ChevronRightIcon fontSize="small" />
              </button>
            </div>
          ) : null}
        </div>
      )}

      <div className={`tab-team-pager-grid ${gridClassName}`}>
        {visibleItems.map((item, index) => renderItem(item, showNav ? pageStart + index : index))}
      </div>
    </div>
  );
}

export default TeamAttendancePager;
